import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseUrl } from "store/baseUrls";
import { xApiKey } from "store/apiKeys";
import type { RootState } from "..";
import type { Result } from "store/api.type";
import type { Track } from "store/track/track.model";
import type { Artist } from "./artist.model";
import type { Album } from "store/album/album.model";

export const artistApi = createApi({
  reducerPath: "artistApi",
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      headers.set("Accept", "application/json");
      headers.set("X-Api-Key", xApiKey);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getArtists: builder.query<Artist[], void>({
      query: () => "/v1/Artist",
      transformResponse: (data: Result<Artist>) => data.list,
    }),
    getForeignArtists: builder.query<
      Artist[],
      { offset?: number; limit?: number }
    >({
      query: ({ offset = 0, limit = 20 }) =>
        `/v1/artist?mode=en&maxSize=${limit}&offset=${offset}`,
      transformResponse: (data: Result<Artist>) => data.list,
    }),
    getIranianArtists: builder.query<
      Artist[],
      { offset?: number; limit?: number }
    >({
      query: ({ offset = 0, limit = 20 }) =>
        `/v1/artist?mode=fa&maxSize=${limit}&offset=${offset}`,
      transformResponse: (data: Result<Artist>) => data.list,
    }),
    getArtist: builder.query<Artist, { artistId: string }>({
      query: ({ artistId }) => `/v1/Artist/${artistId}`,
    }),
    getArtistTracks: builder.query<
      Result<Track>,
      { artistId: string; offset: number; limit: number }
    >({
      query: ({ artistId, offset, limit }) =>
        `/v1/Artist/${artistId}/tracks?maxSize=${limit}&offset=${offset}`,
    }),
    getArtistAlbums: builder.query<Album[], { artistId: string }>({
      query: ({ artistId }) => `/v1/Artist/${artistId}/albums`,
      transformResponse: (data: Result<Album>) => data.list,
    }),
    getArtistBestTracks: builder.query<Track[], { artistId: string }>({
      query: ({ artistId }) =>
        `/v1/Artist/${artistId}/tracks?maxSize=16&orderBy=popularity&order=desc`,
      transformResponse: (data: Result<Track>) => data.list,
    }),
  }),
});

export const {
  useGetArtistsQuery,
  useGetArtistQuery,
  useGetForeignArtistsQuery,
  useGetIranianArtistsQuery,
  useGetArtistTracksQuery,
  useGetArtistAlbumsQuery,
  useGetArtistBestTracksQuery,
  util: artistApiUtil,
} = artistApi;
